
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { toast } from "sonner";

export const useHeatmapData = (selectedPage: string) => {
  // Create sample interactions for heat map
  const createSampleInteractions = async (pageUrl: string) => {
    try {
      if (!pageUrl) return;
      
      const sampleInteractions = [];
      const interactionTypes = ['click', 'mouseover', 'scroll'];
      
      // Generate random interactions
      for (let i = 0; i < 200; i++) {
        const interactionType = interactionTypes[Math.floor(Math.random() * interactionTypes.length)];
        
        // Create the base interaction object
        const interaction: {
          page_url: string;
          interaction_type: string;
          x_coordinate: number;
          y_coordinate: number;
          device_type: string;
          session_id: string;
          viewport_width: number;
          viewport_height: number;
          scroll_depth?: number; // Make scroll_depth optional
        } = {
          page_url: pageUrl,
          interaction_type: interactionType,
          x_coordinate: Math.floor(Math.random() * 1200),
          y_coordinate: Math.floor(Math.random() * 800),
          device_type: ['desktop', 'mobile', 'tablet'][Math.floor(Math.random() * 3)],
          session_id: `sample-session-${Math.floor(Math.random() * 50)}`,
          viewport_width: 1280,
          viewport_height: 800,
        };
        
        // Add scroll depth for scroll interactions
        if (interactionType === 'scroll') {
          interaction.scroll_depth = Math.floor(Math.random() * 100);
        }
        
        sampleInteractions.push(interaction);
      }
      
      // Insert interactions in batches to avoid request size limits
      const batchSize = 50;
      for (let i = 0; i < sampleInteractions.length; i += batchSize) {
        const batch = sampleInteractions.slice(i, i + batchSize);
        const { error } = await supabase.from("user_interactions").insert(batch);
        if (error) throw error;
      }
      
    } catch (error) {
      console.error("Error creating sample interactions:", error);
      toast.error("Failed to create sample interactions");
    }
  };

  // Fetch all unique pages with interactions
  const { data: pages = [], isLoading: isLoadingPages, error: pagesError } = useQuery({
    queryKey: ['page-urls'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_interactions')
        .select('page_url')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching pages:', error);
        throw error;
      }
      
      // If no data, create sample data for some common pages
      if (!data || data.length === 0) {
        console.warn("No page interaction data found, creating sample data");
        const samplePages = [
          '/', 
          '/about', 
          '/services', 
          '/contact', 
          '/blog',
          '/enterprise',
          '/startup',
          '/government'
        ];
        
        // Create sample interactions for each page
        for (const page of samplePages) {
          await createSampleInteractions(page);
        }
        
        const { data: newData, error: newError } = await supabase
          .from('user_interactions')
          .select('page_url')
          .order('created_at', { ascending: false });
          
        if (newError) throw newError;
        
        // Get unique pages and sort them
        const uniquePages = [...new Set(newData.map(d => d.page_url))].sort();
        return uniquePages;
      }
      
      // Get unique pages and sort them
      const uniquePages = [...new Set(data.map(d => d.page_url))].sort();
      return uniquePages;
    }
  });

  // Fetch interactions for selected page
  const { data: interactions = [], isLoading: isLoadingInteractions, error: interactionsError } = useQuery({
    queryKey: ['heatmap-data', selectedPage],
    queryFn: async () => {
      // If no page is selected, return empty array
      if (!selectedPage) return [];
      
      const { data, error } = await supabase
        .from('user_interactions')
        .select('x_coordinate, y_coordinate, interaction_type')
        .eq('page_url', selectedPage)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching interactions:', error);
        throw error;
      }
      
      return data;
    },
    enabled: !!selectedPage
  });

  // Fetch analytics for selected page
  const { data: pageAnalytics, isLoading: isLoadingAnalytics, error: analyticsError } = useQuery({
    queryKey: ['page-analytics', selectedPage],
    queryFn: async () => {
      // If no page is selected, return null
      if (!selectedPage) return null;
      
      const { data: interactions, error } = await supabase
        .from('user_interactions')
        .select('*')
        .eq('page_url', selectedPage);

      if (error) {
        console.error('Error fetching analytics:', error);
        throw error;
      }

      return {
        page_url: selectedPage,
        total_interactions: interactions.length,
        click_count: interactions.filter(i => i.interaction_type === 'click').length,
        scroll_depth_avg: interactions
          .filter(i => i.scroll_depth)
          .reduce((acc, curr) => acc + (curr.scroll_depth || 0), 0) / 
          interactions.filter(i => i.scroll_depth).length || 0,
        device_breakdown: {
          desktop: interactions.filter(i => i.device_type === 'desktop').length,
          mobile: interactions.filter(i => i.device_type === 'mobile').length,
          tablet: interactions.filter(i => i.device_type === 'tablet').length,
        }
      };
    },
    enabled: !!selectedPage
  });

  // Handle data loading errors
  useEffect(() => {
    if (pagesError) {
      console.error('Error loading pages data:', pagesError);
      toast.error('Failed to load pages data');
    }
    
    if (interactionsError) {
      console.error('Error loading interactions data:', interactionsError);
      toast.error('Failed to load interactions data');
    }
    
    if (analyticsError) {
      console.error('Error loading page analytics:', analyticsError);
      toast.error('Failed to load page analytics');
    }
  }, [pagesError, interactionsError, analyticsError]);

  return {
    pages,
    interactions,
    pageAnalytics,
    isLoading: isLoadingPages || isLoadingInteractions || isLoadingAnalytics
  };
};
